import { useLayoutEffect, useRef, useState } from 'react';
import type { ChartPoint } from './chartGeometry';

const UPDATE_MS = 350;
type Frame = { id: string; points: ChartPoint[] | null };

export function useChartMotion(
  id: string,
  target: ChartPoint[] | null,
  durationMs = UPDATE_MS,
) {
  const [frame, setFrame] = useState<Frame>(() => ({ id, points: target }));
  const drawn = useRef(frame);
  // Equal history on a clock tick must not restart a running transition.
  const signature = JSON.stringify(target);
  useLayoutEffect(() => {
    const points = JSON.parse(signature) as ChartPoint[] | null;
    const previous = drawn.current;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let animation = 0;
    const paint = (value: ChartPoint[] | null) => {
      drawn.current = { id, points: value };
      setFrame(drawn.current);
    };
    const finish = () => {
      cancelAnimationFrame(animation);
      paint(points);
    };
    const from = previous.points;
    if (
      previous.id !== id ||
      !from ||
      !points ||
      from.length !== points.length ||
      durationMs <= 0 ||
      reduced.matches
    )
      finish();
    else {
      const start = performance.now();
      const tick = (now: number) => {
        const progress = Math.min(1, (now - start) / durationMs);
        const blend = 1 - (1 - progress) ** 3;
        paint(
          points.map((point, i) => ({
            x: from[i].x + (point.x - from[i].x) * blend,
            y: from[i].y + (point.y - from[i].y) * blend,
          })),
        );
        if (progress < 1) animation = requestAnimationFrame(tick);
      };
      animation = requestAnimationFrame(tick);
    }
    reduced.addEventListener('change', finish);
    return () => {
      cancelAnimationFrame(animation);
      reduced.removeEventListener('change', finish);
    };
  }, [id, signature, durationMs]);
  // A different token or missing evidence never flashes the previous chart.
  return frame.id === id && target ? (frame.points ?? target) : target;
}
